/** VBAnnagate API client — single source of truth for console data access. */
import { bearerToken } from './auth'

const BASE = import.meta.env.VITE_API_BASE ?? ''
const API_KEY = import.meta.env.VITE_API_KEY ?? ''
const ADMIN_KEY = import.meta.env.VITE_ADMIN_KEY ?? API_KEY

export class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, detail: string) {
    super(detail)
    this.status = status
    this.code = code
  }
}

function authHeaders(isAdmin: boolean): Record<string, string> {
  const token = bearerToken()
  if (token) return { Authorization: `Bearer ${token}` }
  return { 'X-API-Key': isAdmin ? ADMIN_KEY : API_KEY }
}

async function request<T>(path: string, init?: RequestInit & { admin?: boolean }): Promise<T> {
  // FormData bodies must keep the browser's multipart boundary — no content-type override
  const isForm = typeof FormData !== 'undefined' && init?.body instanceof FormData
  const headers: Record<string, string> = {
    ...(isForm ? {} : { 'Content-Type': 'application/json' }),
    ...authHeaders(init?.admin ?? false),
    ...((init?.headers ?? {}) as Record<string, string>),
  }
  const response = await fetch(`${BASE}${path}`, { ...init, headers })
  if (!response.ok) {
    let code = 'http_error'
    let detail = `Request failed with status ${response.status}`
    try {
      const problem = (await response.json()) as { code?: string; detail?: string }
      if (problem.code) code = problem.code
      if (problem.detail) detail = problem.detail
    } catch {
      // body wasn't problem+json; keep defaults
    }
    throw new ApiError(response.status, code, detail)
  }
  return (await response.json()) as T
}

async function requestText(path: string, admin = false): Promise<string> {
  const headers = authHeaders(admin)
  const response = await fetch(`${BASE}${path}`, { headers })
  if (!response.ok) throw new ApiError(response.status, 'export_failed', `Export failed: ${response.status}`)
  return response.text()
}

/** Binary download (PDFs: certificates, filing documents) → blob for object URLs. */
async function requestBlob(path: string): Promise<Blob> {
  const response = await fetch(`${BASE}${path}`, { headers: authHeaders(false) })
  if (!response.ok) throw new ApiError(response.status, 'download_failed', `Download failed: ${response.status}`)
  return response.blob()
}

// ---------- types ----------

export interface CompanyRecord {
  recordId: string
  sourceId: string
  registryNumber: string | null
  legalName: string
  legalForm: string | null
  status: string | null
  incorporationDate: string | null
  registeredDate: string | null
  addressLine: string | null
  city: string | null
  postalCode: string | null
  region: string | null
  countryCode: string | null
  website: string | null
  industryCode: string | null
  industryDesc: string | null
  lei: string | null
  entityId: string | null
  sourceCount: number
  retrievedAt: string | null
}

export interface PageResult<T> {
  items: T[]
  total: number
  page: number
  size: number
  nextCursor?: string | null
}

export interface RiskFlag {
  code: string
  severity: 'high' | 'info' | 'positive'
  message: string
}

export interface Provenance {
  sourceId: string
  registryNumber: string | null
  recordId: string
  retrievedAt: string | null
}

export interface VerifyResult {
  verdict: string
  company: CompanyRecord | null
  riskFlags: RiskFlag[]
  provenance: Provenance[]
  crossCheckSources: number | null
}

export interface Signal {
  signalId: string
  signalType: string
  recordId: string | null
  entityId: string | null
  sourceId: string | null
  eventDate: string | null
  summary: string | null
  payload: Record<string, unknown> | null
}

export interface Me {
  tenantId: string
  email: string
  roles: string[]
  scopes: string[]
  authType: string
}

export interface ApiKeyRow {
  id: string
  key_prefix: string
  status: string
  rate_limit_per_minute: number
  last_used_at: string | null
}

export interface AuditRow {
  id: number
  tenant_id: string | null
  actor: string
  action: string
  resource: string | null
  entry_hash: string
  created_at: string
}

export interface Watchlist {
  id: string
  name: string
  items: number
}

export interface WatchlistItem {
  entity_id: string
  legal_name: string | null
  added_at: string
  signals_30d?: number
}

export interface WebhookRow {
  id: string
  url: string
  events: string[]
  status: string
  delivered: number
  outstanding: number
  dead: number
}

export interface Delivery {
  id: number
  event_type: string
  status: string
  attempts: number
  last_error: string | null
  created_at: string
}

export interface Icp {
  id: string
  name: string
  filters: { country?: string; industry?: string; days?: number }
}

export interface InviteRow {
  id: string
  email: string
  role: string
  status: string
  invited_by: string | null
  created_at: string
}

export interface NotifyChannel {
  id: string
  channel_type: string
  target_url: string
  status: string
  created_at: string
}

export interface FeedbackRow {
  id: number
  tenant_id: string | null
  record_id: string | null
  category: string
  message: string
  status: string
  created_at: string
}

export interface SuppressionRow {
  person_number: string
  reason: string
  requested_by: string
  suppressed_at: string
}

export interface AnnouncementRow {
  id: string
  message: string
  level: string
  active: boolean
  created_at: string
}

export interface SyncRequestRow {
  id: number
  source_id: string
  mode: string
  status: string
  records_synced: number | null
}

export interface AdminSource {
  source_id: string
  country_code: string | null
  name: string
  access_type: string
  enabled: boolean
  update_cadence: string
  record_count: number
  last_sync_at: string | null
}

export interface Partner {
  id: string
  name: string
  tier: string
  commission_pct: string | number
  contact_email: string | null
  status: string
  linked_tenants: number
}

export interface Institution {
  id: string
  name: string
  license_type: string
  sla_tier: string
  status: string
  tenant_name: string | null
}

export interface Tenant {
  id: string
  name: string
  plan: string
  status: string
  users?: number
  verifies_this_month?: number
}

export interface MetaCountry {
  country_code: string
  name_en: string
  /** Present on admin projections only — the public /meta/countries list is LIVE-only. */
  status?: string
  supported_services: string
  freshness_sla: string | null
}

export interface Enrichment {
  companyNumber: string
  source: string
  officers: {
    name: string | null
    role: string | null
    appointedOn: string | null
    resignedOn: string | null
    nationality: string | null
    countryOfResidence: string | null
    identityVerified: boolean | null
  }[]
  psc: {
    name: string | null
    kind: string | null
    naturesOfControl: string[]
    notifiedOn: string | null
    ceasedOn: string | null
    identityVerified: boolean | null
  }[]
  idvVerifiedOfficers: number
  idvTotalOfficers: number
  idvVerifiedPsc: number
  idvTotalPsc: number
  note: string
}

export interface AdminFlag {
  flag_key: string
  enabled: boolean
  description: string | null
}

export interface BatchRowResult {
  id: number
  input_query: string | null
  input_number: string | null
  input_country: string | null
  verdict: string | null
  matched_record_id: string | null
  matched_name: string | null
}

export interface Overview {
  companiesCovered: number
  crossVerified: number
  signals7d: number
  verifiesThisMonth: number
}

export interface Filing {
  transactionId: string | null
  type: string | null
  date: string | null
  description: string | null
  category: string | null
  documentPath: string | null
}

export interface CaseItem {
  id: number
  label: string
  done: boolean
}

export interface CaseDocument {
  id: string
  filename: string
  contentType: string
  sizeBytes: number
  uploadedBy: string | null
  createdAt: string
}

export interface KybCase {
  id: string
  recordId: string | null
  legalName: string
  countryCode: string | null
  status: string
  assignee: string | null
  notes: string | null
  createdBy: string | null
  createdAt: string
  updatedAt: string | null
  items?: CaseItem[]
  documents?: CaseDocument[]
}

// ---------- api ----------

export const api = {
  me: () => request<Me>('/v1/me'),

  // meta (public)
  metaCountries: () => request<MetaCountry[]>('/v1/meta/countries'),
  metaStatus: () =>
    request<{
      api: string
      database: string
      databaseLatencyMs: number
      syncQueue: Record<string, number>
      sources: { source_id: string; country_code: string | null; freshness: string; record_count: number; last_sync_at: string | null }[]
    }>('/v1/meta/status'),
  metaFlags: () => request<Record<string, boolean>>('/v1/meta/flags'),
  metaAnnouncements: () => request<{ message: string; level: string }[]>('/v1/meta/announcements'),

  // registry
  searchCompanies: (params: { q?: string; country?: string; status?: string; page?: number; size?: number }) => {
    const qs = new URLSearchParams()
    if (params.q) qs.set('q', params.q)
    if (params.country) qs.set('country', params.country)
    if (params.status) qs.set('status', params.status)
    qs.set('page', String(params.page ?? 1))
    qs.set('size', String(params.size ?? 25))
    return request<PageResult<CompanyRecord>>(`/v1/companies/search?${qs}`)
  },
  companyProfile: (recordId: string) =>
    request<{ company: CompanyRecord; crossVerified: boolean; provenance: CompanyRecord[] }>(
      `/v1/companies/${recordId}`,
    ),
  screeningMatch: (name: string, country: string) =>
    request<{ loaded: boolean; strongMatch: boolean; candidates: { name: string; dataset: string; similarity: number }[] }>(
      `/v1/screening/match?name=${encodeURIComponent(name)}&country=${encodeURIComponent(country)}`),
  enrichment: (recordId: string) =>
    request<Enrichment>(`/v1/companies/${recordId}/enrichment`),
  score: (recordId: string) =>
    request<{ recordId: string; score: number; grade: string; factors: { code: string; points: number; message: string }[] }>(
      `/v1/companies/${recordId}/score`,
    ),
  graph: (recordId: string, depth: number) =>
    request<{
      root: string
      nodes: { id: string; type: string; name: string; number: string }[]
      edges: { from: string; to: string; relation: string; nature: string }[]
      depthUsed: number
    }>(`/v1/companies/${recordId}/graph?depth=${depth}`),
  verify: (body: { query?: string; registryNumber?: string; country?: string }) =>
    request<VerifyResult>('/v1/verify', { method: 'POST', body: JSON.stringify(body) }),

  // signals
  signals: (params: { type?: string; country?: string; since?: string; size?: number }) => {
    const qs = new URLSearchParams()
    if (params.type) qs.set('type', params.type)
    if (params.country) qs.set('country', params.country)
    if (params.since) qs.set('since', params.since)
    qs.set('size', String(params.size ?? 25))
    return request<PageResult<Signal>>(`/v1/signals?${qs}`)
  },

  // usage / onboarding / analytics / AI
  usage: () => request<{ used: number; limit: number; percentUsed: number; plan: string }>('/v1/usage'),
  overview: () => request<Overview>('/v1/overview'),
  onboarding: () =>
    request<{ steps: Record<string, boolean>; completed: number; total: number }>('/v1/onboarding'),
  analytics: (country: string, days: number) =>
    request<{
      byIndustry: { industry: string; cnt: number }[]
      byCity: { city: string; cnt: number }[]
      byDay: { day: string; cnt: number }[]
    }>(`/v1/analytics/registrations?country=${country}&days=${days}`),
  aiQuery: (question: string) =>
    request<{ interpretation: Record<string, unknown>; results: Record<string, unknown>[] }>('/v1/ai/query', {
      method: 'POST',
      body: JSON.stringify({ question }),
    }),

  // batches
  createBatch: (rows: { query?: string; registryNumber?: string; country?: string }[]) =>
    request<{ batchId: string; total: number; status: string }>('/v1/verify/batches', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),
  batchStatus: (id: string) =>
    request<{
      batchId: string
      status: string
      total: number
      processed: number
      verified: number
      found: number
      notFound: number
    }>(`/v1/verify/batches/${id}`),
  batchRows: (id: string, page = 1, size = 100) =>
    request<BatchRowResult[]>(`/v1/verify/batches/${id}/rows?page=${page}&size=${size}`),

  // exports (metered)
  exportSignalsCsv: (country: string, days: number) =>
    requestText(`/v1/export/signals.csv?country=${encodeURIComponent(country)}&days=${days}`),
  exportCompaniesCsv: (q: string, country: string) =>
    requestText(`/v1/export/companies.csv?q=${encodeURIComponent(q)}&country=${encodeURIComponent(country)}`),

  // watchlists
  listWatchlists: () => request<Watchlist[]>('/v1/watchlists'),
  createWatchlist: (name: string) =>
    request<{ id: string; name: string }>('/v1/watchlists', { method: 'POST', body: JSON.stringify({ name }) }),
  renameWatchlist: (id: string, name: string) =>
    request<{ id: string; name: string }>(`/v1/watchlists/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),
  deleteWatchlist: (id: string) => request(`/v1/watchlists/${id}`, { method: 'DELETE' }),
  watchlistItems: (id: string) => request<WatchlistItem[]>(`/v1/watchlists/${id}/items`),
  addWatchlistItem: (id: string, entityId: string, legalName: string) =>
    request(`/v1/watchlists/${id}/items`, { method: 'POST', body: JSON.stringify({ entityId, legalName }) }),
  removeWatchlistItem: (id: string, entityId: string) =>
    request(`/v1/watchlists/${id}/items/${entityId}`, { method: 'DELETE' }),
  watchlistItemSignals: (id: string, entityId: string, days = 90) =>
    request<Signal[]>(`/v1/watchlists/${id}/items/${entityId}/signals?days=${days}`),

  // webhooks
  listWebhooks: () => request<WebhookRow[]>('/v1/webhooks'),
  createWebhook: (url: string, events: string[]) =>
    request<{ id: string; secret: string }>('/v1/webhooks', {
      method: 'POST',
      body: JSON.stringify({ url, events }),
    }),
  patchWebhook: (id: string, body: { url?: string; events?: string[]; status?: string }) =>
    request(`/v1/webhooks/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteWebhook: (id: string) => request(`/v1/webhooks/${id}`, { method: 'DELETE' }),
  webhookDeliveries: (id: string) => request<Delivery[]>(`/v1/webhooks/${id}/deliveries`),
  testWebhook: (id: string) => request(`/v1/webhooks/${id}/test`, { method: 'POST' }),
  rotateWebhookSecret: (id: string) =>
    request<{ id: string; secret: string }>(`/v1/webhooks/${id}/rotate-secret`, { method: 'POST' }),
  replayDead: (id: string) => request<{ id: string; requeued: number }>(`/v1/webhooks/${id}/replay-dead`, { method: 'POST' }),

  // API keys
  listApiKeys: () => request<ApiKeyRow[]>('/v1/api-keys'),
  createApiKey: () =>
    request<{ id: string; prefix: string; secret: string }>('/v1/api-keys', { method: 'POST', body: '{}' }),
  rotateApiKey: (id: string) =>
    request<{ id: string; secret: string }>(`/v1/api-keys/${id}/rotate`, { method: 'POST' }),
  revokeApiKey: (id: string) => request(`/v1/api-keys/${id}`, { method: 'DELETE' }),

  // ICPs
  listIcps: () => request<Icp[]>('/v1/saved-filters'),
  createIcp: (body: { name: string; country?: string; industry?: string; days?: number; verifiedOnly?: boolean; query?: string }) =>
    request<{ id: string; name: string }>('/v1/saved-filters', { method: 'POST', body: JSON.stringify(body) }),
  deleteIcp: (id: string) => request(`/v1/saved-filters/${id}`, { method: 'DELETE' }),
  runIcp: (id: string) => request<Signal[]>(`/v1/saved-filters/${id}/run`),

  // notify channels
  listNotifyChannels: () => request<NotifyChannel[]>('/v1/notify-channels'),
  createNotifyChannel: (body: { channelType: string; targetUrl: string }) =>
    request<{ id: string; channelType: string }>('/v1/notify-channels', { method: 'POST', body: JSON.stringify(body) }),
  deleteNotifyChannel: (id: string) => request(`/v1/notify-channels/${id}`, { method: 'DELETE' }),
  testNotifyChannel: (id: string) =>
    request<{ id: string; delivered: boolean }>(`/v1/notify-channels/${id}/test`, { method: 'POST' }),

  // digest preferences (watchlist email digests; sending requires SMTP)
  digestPrefs: () => request<{ frequency: string }>('/v1/me/digest'),
  setDigestPrefs: (frequency: string) =>
    request<{ frequency: string }>('/v1/me/digest', { method: 'PATCH', body: JSON.stringify({ frequency }) }),

  // company timeline (S-UIX-12) + UK filings (S-UIX-17) + certificate (S-UIX-14)
  timeline: (recordId: string, size = 100) =>
    request<Signal[]>(`/v1/companies/${recordId}/timeline?size=${size}`),
  filings: (recordId: string) =>
    request<{ companyNumber: string; enabled: boolean; note: string; filings: Filing[] }>(`/v1/companies/${recordId}/filings`),
  downloadFilingDocument: (recordId: string, documentPath: string) =>
    requestBlob(`/v1/companies/${recordId}/filings/document?path=${encodeURIComponent(documentPath)}`),
  certificateUrl: (recordId: string) => `${BASE}/v1/public/certificate/${recordId}.pdf`,

  // billing (S-UIX-9)
  billingCheckout: (plan: string) =>
    request<{ url: string }>('/v1/billing/checkout-session', { method: 'POST', body: JSON.stringify({ plan }) }),

  // KYB cases (S-UIX-16)
  listCases: (status?: string) =>
    request<KybCase[]>(`/v1/cases${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  createCase: (body: { recordId?: string; legalName?: string; countryCode?: string }) =>
    request<KybCase>('/v1/cases', { method: 'POST', body: JSON.stringify(body) }),
  caseDetail: (id: string) => request<KybCase>(`/v1/cases/${id}`),
  patchCase: (id: string, body: { status?: string; assignee?: string | null; notes?: string }) =>
    request<KybCase>(`/v1/cases/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  addCaseItem: (id: string, label: string) =>
    request<KybCase>(`/v1/cases/${id}/items`, { method: 'POST', body: JSON.stringify({ label }) }),
  patchCaseItem: (id: string, itemId: number, done: boolean) =>
    request<KybCase>(`/v1/cases/${id}/items/${itemId}`, { method: 'PATCH', body: JSON.stringify({ done }) }),
  uploadCaseDocument: (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<KybCase>(`/v1/cases/${id}/documents`, { method: 'POST', body: form })
  },
  downloadCaseDocument: (id: string, documentId: string) => requestBlob(`/v1/cases/${id}/documents/${documentId}/content`),
  deleteCaseDocument: (id: string, documentId: string) =>
    request(`/v1/cases/${id}/documents/${documentId}`, { method: 'DELETE' }),

  // feedback
  reportIssue: (body: { recordId: string; category: string; message: string }) =>
    request<{ status: string; category: string }>('/v1/feedback', { method: 'POST', body: JSON.stringify(body) }),

  // team invites
  listInvites: () => request<InviteRow[]>('/v1/invites'),
  createInvite: (body: { email: string; role: string }) =>
    request<{ email: string; role: string; status: string }>('/v1/invites', { method: 'POST', body: JSON.stringify(body) }),
  revokeInvite: (id: string) => request(`/v1/invites/${id}`, { method: 'DELETE' }),

  // admin
  admin: {
    tenants: () => request<Tenant[]>('/v1/admin/tenants'),
    flags: () => request<AdminFlag[]>('/v1/admin/flags'),
    toggleFlag: (key: string, enabled: boolean) =>
      request<{ flag: string; enabled: boolean }>(`/v1/admin/flags?key=${encodeURIComponent(key)}&enabled=${enabled}`, {
        method: 'POST',
      }),
    announcements: () => request<AnnouncementRow[]>('/v1/admin/announcements'),
    createAnnouncement: (message: string, level: string) =>
      request<{ id: string }>(`/v1/admin/announcements?message=${encodeURIComponent(message)}&level=${level}`, {
        method: 'POST',
      }),
    deactivateAnnouncement: (id: string) => request(`/v1/admin/announcements/${id}`, { method: 'DELETE' }),
    audit: (limit: number) => request<AuditRow[]>(`/v1/admin/audit?limit=${limit}`),
    feedback: () => request<FeedbackRow[]>('/v1/feedback/admin?limit=100'),
    resolveFeedback: (id: number, resolution: string) =>
      request(`/v1/feedback/admin/${id}/resolve`, { method: 'POST', body: JSON.stringify({ resolution }) }),
    dsrSuppressions: () => request<SuppressionRow[]>('/v1/admin/dsr/suppressions'),
    dsrSuppress: (personNumber: string, reason: string) =>
      request<{ personNumber: string; suppressed: boolean }>(
        `/v1/admin/dsr/suppress?personNumber=${encodeURIComponent(personNumber)}&reason=${encodeURIComponent(reason)}`,
        { method: 'POST' },
      ),
    syncRequests: () => request<SyncRequestRow[]>('/v1/admin/config/sync-requests'),
    requestSync: (sourceId: string, mode: string) =>
      request<{ id: number; sourceId: string; mode: string; status: string }>(
        `/v1/admin/config/sources/${sourceId}/sync?mode=${mode}`,
        { method: 'POST' },
      ),
    sources: () => request<AdminSource[]>('/v1/admin/config/sources'),
    institutions: () => request<Institution[]>('/v1/admin/institutions'),
    partners: () => request<Partner[]>('/v1/admin/partners'),
    createPartner: (body: { name: string; tier: string }) =>
      request<{ id: string; name: string; tier: string }>('/v1/admin/partners', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    createCountry: (body: { countryCode: string; nameEn: string }) =>
      request<{ countryCode: string; status: string }>('/v1/admin/config/countries', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    setCountryStatus: (code: string, status: string) =>
      request<{ countryCode: string; status: string }>(
        `/v1/admin/config/countries/${code}/status?status=${encodeURIComponent(status)}`,
        { method: 'PATCH' },
      ),
  },
}
